import { useState, useRef, useEffect } from "react";
import { Head, router } from "@inertiajs/react";
import { motion } from "framer-motion";
import {
  Card,
  CardContent,
  CardHeader,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { ShoppingCart, Shield, Truck, ZoomIn, ChevronLeft, ChevronRight, Heart, Share2 } from "lucide-react";
import MainLayout from "../../components/MainLayout";
import Breadcrumb from "../../components/Breadcrumb";
import { ExclusiveProduct, StoreSettings } from "../../types";

// Declare gtag function for Google Analytics
declare global {
  function gtag(...args: any[]): void;
}

interface Product {
  id: number;
  name: string;
  description: string;
  price: string | number;
  images: string[];
  category_id: number;
  category_name: string;
  brand_name?: string;
  specifications?: Record<string, string>;
  warranty?: {
    duration: string;
    coverage: string;
    support: string;
    returns: string;
  };
  cart_enabled: boolean;
  market_price?: number;
}

interface ProductDetailProps {
  product: Product;
  store_settings: StoreSettings;
  exclusive_products: ExclusiveProduct[];
}

const ProductDetail = ({
  product,
  store_settings,
  exclusive_products,
}: ProductDetailProps) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState("description");
  const [isZoomed, setIsZoomed] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const imageRef = useRef<HTMLImageElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const containerRef = useRef<HTMLDivElement>(null);

  // Handle image zoom functionality with smooth tracking
  const handleImageMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current || !isZoomed) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    // Clamp values between 0 and 100 for smooth movement
    const clampedX = Math.max(0, Math.min(100, x));
    const clampedY = Math.max(0, Math.min(100, y));
    
    setMousePosition({ x: clampedX, y: clampedY });
  };

  // Navigate images with keyboard
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && selectedImageIndex > 0) {
        setSelectedImageIndex(selectedImageIndex - 1);
      } else if (e.key === 'ArrowRight' && selectedImageIndex < product.images.length - 1) {
        setSelectedImageIndex(selectedImageIndex + 1);
      } else if (e.key === 'Escape') {
        setIsZoomed(false);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedImageIndex, product.images.length]);

  const nextImage = () => {
    if (selectedImageIndex < product.images.length - 1) {
      setSelectedImageIndex(selectedImageIndex + 1);
    }
  };

  const prevImage = () => {
    if (selectedImageIndex > 0) {
      setSelectedImageIndex(selectedImageIndex - 1);
    }
  };

  // Generate WhatsApp message for quotation
  const generateWhatsAppMessage = () => {
    const message = `Hola! Me interesa cotizar el siguiente producto:\n\n` +
      `📦 *${product.name}*\n` +
      `🏷️ Categoría: ${product.category_name}\n` +
      `💰 Precio referencial: S/. ${(Number(product.price) || 0).toFixed(2)}\n\n` +
      `¿Podrían proporcionarme más información y disponibilidad?\n\n` +
      `Gracias!`;
    
    return encodeURIComponent(message);
  };

  const handleWhatsAppQuote = () => {
    // Google Ads conversion tracking
    if (typeof gtag !== 'undefined') {
      gtag('event', 'conversion', {
        'send_to': 'AW-17685379982/9ss-CLew4rYbEI73hfFB',
        'value': 0,
        'currency': 'PEN',
        'transaction_id': ''
      });
    }

    const message = generateWhatsAppMessage();
    const whatsappUrl = `https://wa.me/${store_settings.whatsapp_number}?text=${message}`;
    window.open(whatsappUrl, "_blank");
  };

  const handleShare = async () => {
    const url = window.location.href;
    const title = product.name;
    const text = `Mira este producto: ${product.name}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text,
          url,
        });
      } catch (error) {
        console.log('Error sharing:', error);
        fallbackShare(url, title);
      }
    } else {
      fallbackShare(url, title);
    }
  };

  const fallbackShare = (url: string, title: string) => {
    const shareText = `${title}\n${url}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(shareText).then(() => {
        alert('Enlace copiado al portapapeles');
      }).catch(() => {
        prompt('Copia este texto:', shareText);
      });
    } else {
      prompt('Copia este texto:', shareText);
    }
  };

  return (
    <MainLayout
      store_settings={store_settings}
      exclusiveProducts={exclusive_products}
    >
      <Head title={product.name} />
      <div className="container mx-auto px-4 py-8">
        <Breadcrumb
          items={[
            {
              label: product.category_name,
              href: `/products?category_id=${product.category_id}`,
            },
            { label: product.name },
          ]}
        />
        <div className="grid md:grid-cols-2 gap-12 mb-8">
          {/* Enhanced Product Images Gallery */}
          <div className="space-y-4 overflow-hidden">
            {/* Main Image Container */}
            <Card className="overflow-hidden relative group">
              <CardHeader className="p-0 relative">
                <div 
                  ref={containerRef}
                  className="aspect-square w-full overflow-hidden bg-white dark:bg-neutral-800 relative cursor-zoom-in"
                  onMouseMove={handleImageMouseMove}
                  onMouseEnter={() => setIsZoomed(true)}
                  onMouseLeave={() => {
                    setIsZoomed(false);
                    setMousePosition({ x: 50, y: 50 });
                  }}
                  onClick={() => setIsZoomed(!isZoomed)}
                >
                  <img
                    ref={imageRef}
                    src={
                      product.images[selectedImageIndex] ||
                      "https://picsum.photos/600"
                    }
                    alt={product.name}
                    className={`w-full h-full object-contain transition-all duration-200 ease-out ${
                      isZoomed ? 'scale-150' : 'scale-100'
                    }`}
                    style={{
                      objectFit: 'contain',
                      objectPosition: 'center',
                      transformOrigin: `${mousePosition.x}% ${mousePosition.y}%`,
                      transition: isZoomed ? 'transform 0.1s ease-out' : 'transform 0.3s ease-out'
                    }}
                  />
                  
                  {/* Navigation Arrows */}
                  {product.images.length > 1 && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          prevImage();
                        }}
                        disabled={selectedImageIndex === 0}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white dark:bg-black/80 dark:hover:bg-black rounded-full p-2 transition-all duration-200 opacity-0 group-hover:opacity-100 disabled:opacity-30 disabled:cursor-not-allowed shadow-lg"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          nextImage();
                        }}
                        disabled={selectedImageIndex === product.images.length - 1}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white dark:bg-black/80 dark:hover:bg-black rounded-full p-2 transition-all duration-200 opacity-0 group-hover:opacity-100 disabled:opacity-30 disabled:cursor-not-allowed shadow-lg"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </>
                  )}

                  {/* Zoom Icon */}
                  <div className="absolute top-4 right-4 bg-white/80 dark:bg-black/80 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <ZoomIn className="h-4 w-4" />
                  </div>

                  {/* Image Counter */}
                  {product.images.length > 1 && (
                    <div className="absolute bottom-4 left-4 bg-black/70 text-white text-sm px-3 py-1 rounded-full">
                      {selectedImageIndex + 1} / {product.images.length}
                    </div>
                  )}
                </div>
              </CardHeader>
            </Card>

            {/* Thumbnail Gallery */}
            {product.images.length > 1 && (
              <div className="flex space-x-2 overflow-x-auto pb-2">
                {product.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImageIndex(index)}
                    className={`flex-shrink-0 w-16 h-16 lg:w-20 lg:h-20 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                      selectedImageIndex === index 
                        ? "border-primary shadow-lg" 
                        : "border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500"
                    }`}
                  >
                    <img
                      src={image}
                      alt={`${product.name} ${index + 1}`}
                      className="w-full h-full object-contain bg-white dark:bg-neutral-800"
                      style={{ 
                        objectFit: 'contain',
                        objectPosition: 'center'
                      }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Enhanced Product Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="space-y-6 overflow-visible"
          >
            {/* Header Section - Professional Design */}
            <div className="relative px-2">
              <div className="relative bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl rounded-3xl p-8 shadow-xl border border-slate-200 dark:border-slate-700">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-slate-800 text-white shadow-lg">
                      {product.category_name}
                    </span>
                    {product.brand_name && (
                      <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                        {product.brand_name}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setIsFavorite(!isFavorite)}
                        className={`p-3 rounded-full transition-all duration-300 hover:scale-110 ${
                          isFavorite
                            ? 'bg-red-500 text-white shadow-lg'
                            : 'bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm text-slate-600 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-800 shadow-lg'
                        }`}
                    >
                      <Heart className={`h-5 w-5 ${isFavorite ? 'fill-current' : ''}`} />
                    </button>
                    <button
                      onClick={handleShare}
                      className="p-3 rounded-full bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm text-slate-600 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-800 shadow-lg transition-all duration-300 hover:scale-110"
                    >
                      <Share2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <h1 className="text-2xl lg:text-3xl font-bold mb-4 leading-tight text-slate-900 dark:text-white">
                  {product.name}
                </h1>

                <p className="text-slate-700 dark:text-slate-300 text-base leading-relaxed mb-4">
                  {product.description}
                </p>

                {/* Product Status Badge */}
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex items-center gap-2 px-3 py-1 bg-slate-100 dark:bg-slate-800 rounded-full">
                    <div className="w-2 h-2 bg-slate-500 rounded-full"></div>
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Producto disponible</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Price Section - Professional Design */}
            <div className="relative px-2">
              <div className="relative bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-slate-200 dark:border-slate-700">
                <div className="space-y-6">
                  {/* Price Display */}
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wide">Precio Online</span>
                        <span className="text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white">
                          S/. {(Number(product.price) || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wide">Precio Regular</span>
                        <span className="text-xl text-slate-500 dark:text-slate-400 line-through font-medium">
                          S/. {product.market_price ? (Number(product.market_price) || 0).toFixed(2) : ((Number(product.price) || 0) * 1.3).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Savings Badge */}
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 rounded-full font-medium text-sm">
                    <span className="text-base">💰</span>
                    Ahorras: S/. {(product.market_price ? ((Number(product.market_price) || 0) - (Number(product.price) || 0)) : ((Number(product.price) || 0) * 0.3)).toFixed(2)}
                  </div>

                  {/* Professional Features Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-3 p-3 bg-white/50 dark:bg-slate-800/50 rounded-xl backdrop-blur-sm">
                      <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-bold">✓</span>
                      </div>
                      <span className="text-xs font-medium text-slate-700 dark:text-slate-300">IGV incluido</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-white/50 dark:bg-slate-800/50 rounded-xl backdrop-blur-sm">
                      <div className="w-8 h-8 bg-slate-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs">🚚</span>
                      </div>
                      <span className="text-xs font-medium text-slate-700 dark:text-slate-300">Envío express</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-white/50 dark:bg-slate-800/50 rounded-xl backdrop-blur-sm">
                      <div className="w-8 h-8 bg-slate-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs">📦</span>
                      </div>
                      <span className="text-xs font-medium text-slate-700 dark:text-slate-300">Disponible</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quantity and Actions */}
            <div className="space-y-4">
              {product.cart_enabled && (
                <div className="flex items-center gap-4">
                  <span className="font-medium text-slate-700 dark:text-slate-300">Cantidad:</span>
                  <div className="flex items-center border-2 border-slate-200 dark:border-slate-600 rounded-xl overflow-hidden bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all duration-200 font-semibold text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
                    >
                      −
                    </button>
                    <span className="px-6 py-3 border-x border-slate-200 dark:border-slate-600 font-bold text-slate-800 dark:text-slate-200 min-w-[3rem] text-center">
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all duration-200 font-semibold text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
                    >
                      +
                    </button>
                  </div>
                </div>
              )}

            <div className="flex flex-col sm:flex-row gap-3">
              {product.cart_enabled ? (
                <>
                  <Button
                    size="lg"
                    className="flex-1 bg-slate-900 hover:bg-slate-800 text-white font-semibold py-3"
                    onClick={() =>
                      router.post(
                        "/cart/items",
                        { product_id: product.id, quantity },
                        { preserveScroll: true },
                      )
                    }
                  >
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    Añadir al carrito
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="flex-1 font-semibold py-3 border-slate-300 text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
                    onClick={handleWhatsAppQuote}
                  >
                    <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.514"/>
                    </svg>
                    💬 Consultar por WhatsApp
                  </Button>
                </>
              ) : (
                <Button
                  size="lg"
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold py-3"
                  onClick={handleWhatsAppQuote}
                >
                  <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.514"/>
                  </svg>
                  Solicitar Cotización por WhatsApp
                </Button>
              )}
            </div>
            </div>

            {/* Enhanced Quick Info */}
            <div className="px-2">
              <Card>
                <CardContent className="p-4">
                <h3 className="font-semibold mb-3">Información de entrega y garantía</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="mt-1">
                      <Shield className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium">Garantía oficial</p>
                      <p className="text-sm text-muted-foreground">
                        {product.warranty?.duration || "Garantía de fábrica incluida"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="mt-1">
                      <Truck className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium">Envío gratuito</p>
                      <p className="text-sm text-muted-foreground">
                        Entrega de 2-5 días hábiles en Lima Metropolitana
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="mt-1">
                      <Shield className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium">Soporte técnico</p>
                      <p className="text-sm text-muted-foreground">
                        Atención personalizada post-venta
                      </p>
                    </div>
                  </div>
                </div>
               </CardContent>
             </Card>
            </div>
           </motion.div>
        </div>

        {/* Enhanced Tabs Section */}
        <Card className="shadow-lg">
          <CardContent className="p-0">
            <div className="border-b bg-neutral-50 dark:bg-neutral-800">
              <div className="flex overflow-x-auto">
                <button
                  onClick={() => setActiveTab("description")}
                  className={`px-6 py-4 font-medium text-sm border-b-2 transition-all duration-200 whitespace-nowrap ${
                    activeTab === "description"
                      ? "border-primary text-primary bg-white dark:bg-neutral-900"
                      : "border-transparent text-muted-foreground hover:text-foreground hover:bg-white/50 dark:hover:bg-neutral-700"
                  }`}
                >
                  📋 Descripción detallada
                </button>
                {product.specifications &&
                  Object.keys(product.specifications).length > 0 && (
                    <button
                      onClick={() => setActiveTab("specifications")}
                      className={`px-6 py-4 font-medium text-sm border-b-2 transition-all duration-200 whitespace-nowrap ${
                        activeTab === "specifications"
                          ? "border-primary text-primary bg-white dark:bg-neutral-900"
                          : "border-transparent text-muted-foreground hover:text-foreground hover:bg-white/50 dark:hover:bg-neutral-700"
                      }`}
                    >
                      ⚙️ Especificaciones técnicas
                    </button>
                  )}
                {product.warranty && (
                  <button
                    onClick={() => setActiveTab("warranty")}
                    className={`px-6 py-4 font-medium text-sm border-b-2 transition-all duration-200 whitespace-nowrap ${
                      activeTab === "warranty"
                        ? "border-primary text-primary bg-white dark:bg-neutral-900"
                        : "border-transparent text-muted-foreground hover:text-foreground hover:bg-white/50 dark:hover:bg-neutral-700"
                    }`}
                  >
                    🛡️ Garantía y soporte
                  </button>
                )}
                <button
                  onClick={() => setActiveTab("quote")}
                  className={`px-6 py-4 font-medium text-sm border-b-2 transition-all duration-200 whitespace-nowrap ${
                    activeTab === "quote"
                      ? "border-primary text-primary bg-white dark:bg-neutral-900"
                      : "border-transparent text-muted-foreground hover:text-foreground hover:bg-white/50 dark:hover:bg-neutral-700"
                  }`}
                >
                  💬 Solicitar cotización
                </button>
              </div>
            </div>

            <div className="p-6 lg:p-8">
              {activeTab === "description" && (
                <div className="animate-fadeIn">
                  <div className="prose max-w-none">
                    <h3 className="text-xl font-semibold mb-4 text-foreground">
                      Descripción del producto
                    </h3>
                    <div className="space-y-4 text-muted-foreground leading-relaxed">
                      <p>{product.description}</p>
                      <div className="grid md:grid-cols-2 gap-6 mt-8">
                        <div className="bg-neutral-50 dark:bg-neutral-800 p-4 rounded-lg">
                          <h4 className="font-semibold text-foreground mb-2">🎯 Características principales</h4>
                          <ul className="space-y-1 text-sm">
                            <li>• Diseño innovador y funcional</li>
                            <li>• Materiales de alta calidad</li>
                            <li>• Tecnología de vanguardia</li>
                            <li>• Fácil instalación y uso</li>
                          </ul>
                        </div>
                        <div className="bg-neutral-50 dark:bg-neutral-800 p-4 rounded-lg">
                          <h4 className="font-semibold text-foreground mb-2">📦 Incluye en la caja</h4>
                          <ul className="space-y-1 text-sm">
                            <li>• {product.name}</li>
                            <li>• Manual de usuario</li>
                            <li>• Certificado de garantía</li>
                            <li>• Accesorios necesarios</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "specifications" && product.specifications && (
                <div className="animate-fadeIn">
                  <h3 className="text-xl font-semibold mb-6 text-foreground">
                    Especificaciones técnicas
                  </h3>
                  <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg overflow-hidden">
                    {Object.entries(product.specifications).map(
                      ([key, value], index) => (
                        <div
                          key={key}
                          className={`flex justify-between items-center py-4 px-6 ${
                            index % 2 === 0 ? 'bg-white dark:bg-neutral-900' : ''
                          }`}
                        >
                          <span className="font-medium text-foreground">
                            {key}
                          </span>
                          <span className="text-muted-foreground font-mono">
                            {value}
                          </span>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}

              {activeTab === "warranty" && product.warranty && (
                <div className="animate-fadeIn">
                  <h3 className="text-xl font-semibold mb-6 text-foreground">
                    Información de garantía y soporte
                  </h3>
                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <Card className="border-green-200 dark:border-green-800">
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <Shield className="h-8 w-8 text-green-600 mt-1" />
                            <div>
                              <h4 className="font-semibold text-lg mb-2">Duración de garantía</h4>
                              <p className="text-muted-foreground mb-4">
                                {product.warranty.duration}
                              </p>
                              <div className="text-sm space-y-1">
                                <p>✓ Cobertura completa de fábrica</p>
                                <p>✓ Reparación o reemplazo gratuito</p>
                                <p>✓ Soporte técnico especializado</p>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      
                      <Card className="border-blue-200 dark:border-blue-800">
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <Shield className="h-8 w-8 text-blue-600 mt-1" />
                            <div>
                              <h4 className="font-semibold text-lg mb-2">Cobertura</h4>
                              <p className="text-muted-foreground">
                                {product.warranty.coverage}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                    
                    <div className="space-y-6">
                      <Card className="border-purple-200 dark:border-purple-800">
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <Shield className="h-8 w-8 text-purple-600 mt-1" />
                            <div>
                              <h4 className="font-semibold text-lg mb-2">Soporte técnico</h4>
                              <p className="text-muted-foreground">
                                {product.warranty.support}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      
                      <Card className="border-orange-200 dark:border-orange-800">
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <Shield className="h-8 w-8 text-orange-600 mt-1" />
                            <div>
                              <h4 className="font-semibold text-lg mb-2">Política de devoluciones</h4>
                              <p className="text-muted-foreground">
                                {product.warranty.returns}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "quote" && (
                <div className="animate-fadeIn">
                  <div className="max-w-2xl mx-auto text-center space-y-8">
                    <div>
                      <h3 className="text-2xl font-bold mb-4 text-foreground">
                        💬 Solicitar Cotización Personalizada
                      </h3>
                      <p className="text-muted-foreground text-lg leading-relaxed">
                        ¿Necesitas información específica sobre este producto? Nuestro equipo de expertos 
                        está listo para ayudarte con una cotización personalizada y resolver todas tus dudas.
                      </p>
                    </div>

                    <Card className="border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950">
                      <CardContent className="p-8">
                        <div className="space-y-6">
                          <div className="text-center">
                            <svg className="h-16 w-16 text-green-600 mx-auto mb-4" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.514"/>
                            </svg>
                            <h4 className="text-xl font-semibold text-green-800 dark:text-green-200 mb-2">
                              Contacto directo por WhatsApp
                            </h4>
                            <p className="text-green-700 dark:text-green-300 mb-6">
                              Recibe una respuesta inmediata de nuestros especialistas
                            </p>
                          </div>

                          <div className="grid md:grid-cols-2 gap-4 text-sm text-green-700 dark:text-green-300">
                            <div className="space-y-2">
                              <p>✅ Cotización sin compromiso</p>
                              <p>✅ Asesoría técnica especializada</p>
                              <p>✅ Información de disponibilidad</p>
                            </div>
                            <div className="space-y-2">
                              <p>✅ Opciones de financiamiento</p>
                              <p>✅ Descuentos por volumen</p>
                              <p>✅ Tiempo de entrega estimado</p>
                            </div>
                          </div>

                          <Button
                            size="lg"
                            className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-4 text-lg"
                            onClick={handleWhatsAppQuote}
                          >
                            <svg className="h-6 w-6 mr-3" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.514"/>
                            </svg>
                            Solicitar Cotización Ahora
                          </Button>

                          <div className="text-center text-sm text-green-600 dark:text-green-400">
                            <p>📱 También puedes llamarnos: {store_settings.whatsapp_number}</p>
                            <p>⏰ Horario de atención: Lunes a Viernes 9:00 AM - 6:00 PM</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default ProductDetail;
